import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
} from '@angular/core';

/**
 * Directive that handles auto-scrolling behavior for chat containers.
 * Automatically scrolls to bottom when new content is added if user was already at bottom.
 * Tracks unread messages when user scrolls away from bottom.
 */
@Directive({
  selector: '[appAutoScroll]',
  standalone: true,
})
export class AutoScrollDirective {
  private readonly _elementRef = inject(ElementRef<HTMLElement>);

  /**
   * Pixel threshold from bottom to consider user "at bottom"
   */
  public readonly scrollThreshold = input<number>(100);

  /**
   * Emits the new unread count when it changes
   */
  public readonly unreadCountChange = output<number>();

  private _wasScrolledToBottom = true;
  private _lastScrollHeight = 0;
  private _unreadCount = 0;

  @HostListener('scroll')
  public onScroll(): void {
    this._updateScrollState();

    if (this._wasScrolledToBottom && this._unreadCount > 0) {
      this._unreadCount = 0;
      this.unreadCountChange.emit(0);
    }
  }

  /**
   * Call this when content changes to handle auto-scroll and unread tracking
   */
  public handleContentChange(): void {
    const element = this._elementRef.nativeElement;
    const currentScrollHeight = element.scrollHeight;

    if (currentScrollHeight !== this._lastScrollHeight) {
      if (this._wasScrolledToBottom) {
        // User was at bottom, keep them at bottom
        element.scrollTop = element.scrollHeight;
      } else {
        // User was scrolled up, increment unread count
        const heightDiff = currentScrollHeight - this._lastScrollHeight;

        if (heightDiff > 0) {
          this._unreadCount++;
          this.unreadCountChange.emit(this._unreadCount);
        }
      }

      this._lastScrollHeight = currentScrollHeight;
    }

    this._updateScrollState();
  }

  /**
   * Scrolls to the bottom of the container
   */
  public scrollToBottom(): void {
    const element = this._elementRef.nativeElement;
    element.scrollTop = element.scrollHeight;

    this._wasScrolledToBottom = true;
    this._unreadCount = 0;
    this._lastScrollHeight = element.scrollHeight;
    this.unreadCountChange.emit(0);
  }

  /**
   * Call this when user sends a message to reset scroll state
   */
  public resetOnSend(): void {
    this._wasScrolledToBottom = true;
    this._unreadCount = 0;
    this.unreadCountChange.emit(0);
  }

  private _updateScrollState(): void {
    const element = this._elementRef.nativeElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    this._wasScrolledToBottom = distanceFromBottom <= this.scrollThreshold();
  }
}
